import type { SetupScreen, SetupStepNavigator } from './setup-screen'

/** The customer-profile screens, in order. The index of a screen is its place in setup. */
export const CUSTOMER_PROFILE_SCREENS = [
  { id: 'reading', transient: true },
  { id: 'profile', transient: false },
] as const satisfies readonly SetupScreen[]

/** One customer-profile screen. A failed draft is the failed variant of `reading`. */
export type CustomerProfileScreen = (typeof CUSTOMER_PROFILE_SCREENS)[number]['id']

export type CustomerProfileMove =
  /** The draft on show has finished and the ring has burst. */
  | 'draft-landed'
  /** Write again on the profile has started a new draft. */
  | 'draft-opened'

/** What the saved data holds, reduced to what the first screen turns on. */
export interface CustomerProfileResumeState {
  /** True when a profile is saved. */
  readonly hasProfile: boolean
  /** True while the latest draft is still working. */
  readonly runLive: boolean
  /** True when the latest draft started after the step before was last confirmed. */
  readonly current: boolean
}

/** The screen the step opens on, taken once from the saved data. */
export function resumeCustomerProfileScreen(
  state: CustomerProfileResumeState,
): CustomerProfileScreen {
  return state.hasProfile && !state.runLive && state.current ? 'profile' : 'reading'
}

/** The screen a move leads to. A move that does not apply to the screen on show keeps it. */
export function moveCustomerProfile(
  from: CustomerProfileScreen,
  move: CustomerProfileMove,
): CustomerProfileScreen {
  switch (move) {
    case 'draft-landed':
      return from === 'reading' ? 'profile' : from
    case 'draft-opened':
      return from === 'profile' ? 'reading' : from
  }
}

export const CUSTOMER_PROFILE_NAVIGATOR: SetupStepNavigator<
  CustomerProfileScreen,
  CustomerProfileMove
> = {
  screens: CUSTOMER_PROFILE_SCREENS,
  move: moveCustomerProfile,
}
